import { useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { usePulseRuns } from '../../../lib/use-pulse-runs';
import { deleteRoutine, type WebRoutine } from '../../../lib/routines';
import { routineStatusBadge } from '../../../lib/routine-status';
import { ROUTINE_DETAILS_PAGE } from '../../../lib/routines-bi';
import { showsTaskCount } from '../../../lib/run-group';
import { Modal } from '../../Modal';
import { AgentDot } from '../../AgentDot';
import { SkillConnectorLine } from '../agents/SkillConnectorLine';
import { Badge, Button, TextButton, Typography } from '../ui';
import { useRoutineCopy } from './use-routine-copy';
import { RoutineEditForm, useRoutineEdit } from './RoutineEditForm';
import styles from './routines.module.css';

// The routine drill-in — a DS centered <Modal>, capped at 650 with the body scrolling inside it (the
// house rule for every dialog; see CLAUDE.md → "Modals"). It answers, in order: what state is this in
// and why, the facts (agent / Space / schedule / next run / activations), what it actually does, and
// what it has been doing lately.
//
// The footer's primary is **Chat about this routine**, which opens a fresh thread seeded with a message
// about it — the same `chatHandoffLink` new-chat handoff Agent Detail's "Remove agent" uses. It is not a
// new API route and must not become one: the assistant is where a routine is reasoned about and changed.
export function RoutineDetailModal({
  routine,
  spaceName,
  startInEdit = false,
  onClose,
}: {
  routine: WebRoutine;
  spaceName?: string;
  /** Opened straight from the card's "Edit routine" — the same modal, showing its form. */
  startInEdit?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi, logModalActionBi } = useGenericBi('routine-details');
  const { titleOf, scheduleText, nextRunText, activityText, runCountText, pauseText } = useRoutineCopy();
  const [editing, setEditing] = useState(startInEdit);

  const runs = usePulseRuns(routine.id);
  useLoadBi(
    'routine-detail-data',
    { isLoading: runs.isLoading, isError: runs.isError },
    { entity: { id: routine.id, type: ENTITY_TYPE.pulse } },
  );

  const edit = useRoutineEdit(routine, () => setEditing(false));
  const title = titleOf(routine);
  const badge = routineStatusBadge(routine);
  const entity = { entity: { id: routine.id, type: ENTITY_TYPE.pulse } };

  const close = () => {
    logModalActionBi({ ...ROUTINE_DETAILS_PAGE, modal_name: 'routine_detail', action: 'close' });
    onClose();
  };

  const onChat = () => {
    logPageClickBi({ ...ROUTINE_DETAILS_PAGE, button_name: 'chat_about_routine' }, entity);
    const link = chatHandoffLink({ kind: 'new', message: t('routines.chatSeed', { name: title }) }, basePath);
    onClose();
    void router.push(link.route, link.path);
  };

  const onDelete = async () => {
    logPageClickBi({ ...ROUTINE_DETAILS_PAGE, button_name: 'delete_routine' }, entity);
    onClose();
    await deleteRoutine(routine.id);
  };

  const pause = pauseText(routine);
  // The Activations row is gated on the run WINDOW being known, not on there being a last run —
  // `runCountText` owns that rule, the same one both phones' `routineRunCountLabel` applies.
  const activations = runCountText(routine);

  return (
    <Modal
      open
      onClose={close}
      size="medium"
      maxHeightPx={650}
      scrollBody
      title={title}
      labelledBy="routine-detail-title"
      headerSeparator
      footerSeparator
      footer={
        editing ? (
          <>
            <Button variant="outlined" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            {/* A routine with no name is not a routine — Save is blocked rather than writing a blank
                title, exactly as both phones' edit sheets block it. */}
            <Button onClick={() => void edit.save()} disabled={edit.saving || !edit.canSave}>
              {t('common.save')}
            </Button>
          </>
        ) : (
          <>
            <TextButton size="small" color="destructive" onClick={() => void onDelete()}>
              {t('routines.action.delete')}
            </TextButton>
            <Button variant="outlined" onClick={() => setEditing(true)}>
              {t('routines.action.edit')}
            </Button>
            <Button onClick={onChat}>{t('routines.action.chat')}</Button>
          </>
        )
      }
    >
      <div className={styles.modalBody}>
        <div className={styles.modalHead}>
          <Badge variant={badge.variant} label={t(badge.labelKey)} />
          {/* Which connected app this routine came with — nothing for a user- or agent-made one. */}
          <SkillConnectorLine slug={routine.sourceConnectorSlug} />
        </div>
        {/* Why it is stopped, in the agent's own words when it left any — otherwise the localized
            reason. Only ever rendered when the server says there IS a reason. */}
        {pause && <p className={styles.pauseNote}>{pause}</p>}

        {editing ? (
          <RoutineEditForm fields={edit.fields} error={edit.error} />
        ) : (
          <>
            <div className={styles.rows}>
              {routine.agentName && (
                <DetailRow label={t('routines.detail.agent')}>
                  <span className={styles.agentTag}>
                    {routine.agentId ? <AgentDot id={routine.agentId} size={15} /> : null}
                    <span className={styles.agentName}>{routine.agentName}</span>
                  </span>
                </DetailRow>
              )}
              {spaceName && <DetailRow label={t('routines.detail.space')}>{spaceName}</DetailRow>}
              <DetailRow label={t('routines.detail.schedule')}>{scheduleText(routine)}</DetailRow>
              {nextRunText(routine) && (
                <DetailRow label={t('routines.detail.nextRun')}>{nextRunText(routine)}</DetailRow>
              )}
              {/* Activations are omitted entirely when the run window did not reach this routine —
                  a bounded read that saw nothing is not evidence that nothing happened. */}
              {activations && <DetailRow label={t('routines.detail.activations')}>{activations}</DetailRow>}
              {activityText(routine) && (
                <DetailRow label={t('routines.detail.lastRun')}>{activityText(routine)}</DetailRow>
              )}
            </div>

            {routine.description?.trim() && (
              <section className={styles.section}>
                <Typography variant="h4" color="primary" component="h3">
                  {t('routines.detail.whatItDoes')}
                </Typography>
                <p className={styles.sectionBody}>{routine.description.trim()}</p>
              </section>
            )}

            <section className={styles.section}>
              <Typography variant="h4" color="primary" component="h3">
                {t('routines.detail.recentRuns')}
              </Typography>
              <RecentRuns runs={runs.isError ? [] : (runs.data ?? null)} />
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{children}</span>
    </div>
  );
}

function RecentRuns({ runs }: { runs: { conversationId: string; spawnedAt: string; tasks: unknown[] }[] | null }) {
  const { t, i18n } = useTranslation();
  if (runs === null) return <span className={styles.muted}>{t('common.loading')}</span>;
  if (runs.length === 0) return <span className={styles.muted}>{t('pulses.runs.empty')}</span>;
  return (
    <div className={styles.runs}>
      {runs.map((run) => {
        const count = run.tasks?.length ?? 0;
        return (
          <div key={run.conversationId} className={styles.runRow}>
            <span>{runDate(run.spawnedAt, i18n?.language)}</span>
            <span>
              {showsTaskCount(count)
                ? count === 1
                  ? t('pulses.runs.tasksCreatedOne')
                  : t('pulses.runs.tasksCreatedOther', { count })
                : t('pulses.runs.nothingToReport')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function runDate(iso: string, locale?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso || '—';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(at);
}
